import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Lock, FileCheck, Hash, Clock, CheckCircle, Upload, AlertTriangle, ArrowRight, Copy, Download, Fingerprint } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_TYPES = [
  { id: 'audio', label: 'Audio', icon: '♪' },
  { id: 'video', label: 'Video', icon: '▶' },
  { id: 'image', label: 'Image', icon: '◻' },
  { id: 'text', label: 'Text', icon: 'T' },
];

export default function VIGProtocolPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('input'); // input, processing, complete
  const [assetTitle, setAssetTitle] = useState('');
  const [assetType, setAssetType] = useState('audio');
  const [assetDescription, setAssetDescription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [certificate, setCertificate] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleWrap = useCallback(async () => {
    if (!assetTitle.trim()) return;
    if (!user) { navigate('/auth'); return; }

    setStep('processing');
    setProcessing(true);
    setProgress(0);

    // Simulate processing with progress
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + Math.random() * 12 + 3;
        if (next >= 100) {
          clearInterval(interval);
          return 100;
        }
        return next;
      });
    }, 150);

    try {
      const res = await axios.post(`${API}/vig/wrap`, {
        title: assetTitle,
        asset_type: assetType,
        description: assetDescription,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Wait for progress to complete visually
      setTimeout(() => {
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => {
          setCertificate(res.data);
          setStep('complete');
          setProcessing(false);
        }, 600);
      }, 2000);
    } catch (err) {
      clearInterval(interval);
      setProcessing(false);
      setStep('input');
    }
  }, [assetTitle, assetType, assetDescription, user, token, navigate]);

  const copyHash = () => {
    if (certificate?.vig_hash) {
      navigator.clipboard.writeText(certificate.vig_hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetForm = () => {
    setStep('input');
    setAssetTitle('');
    setAssetDescription('');
    setCertificate(null);
    setProgress(0);
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="vig-protocol-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <Shield size={14} className="text-[--ch-purple]" />
            <span className="font-label text-[--ch-purple]">CRYPTOGRAPHIC IP PROTECTION</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-3" data-testid="vig-title">
            <span className="neon-purple">.VIG</span> <span className="text-white">Protocol</span>
          </h1>
          <p className="text-base text-[--ch-text-secondary] max-w-lg mx-auto">
            Wrap your intellectual property with cryptographic proof of ownership.
            Tamper-proof. Timestamped. Legally defensible.
          </p>
        </div>

        {/* INPUT STEP */}
        {step === 'input' && (
          <div className="animate-fade-in-up" data-testid="vig-input-step">
            {/* How it works mini-section */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Fingerprint, title: 'Fingerprint', desc: 'Content is hashed with SHA-256 + proprietary .VIG markers', color: '#8B5CF6' },
                { icon: Clock, title: 'Timestamp', desc: 'Immutable timestamp proves when you owned the work', color: '#00F0FF' },
                { icon: FileCheck, title: 'Certificate', desc: 'Downloadable proof of ownership for legal proceedings', color: '#FF00E5' },
              ].map((item, i) => (
                <div key={i} className="ch-card p-5 text-center" data-testid={`vig-step-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3"
                    style={{ background: `${item.color}12`, border: `1px solid ${item.color}25` }}>
                    <item.icon size={20} style={{ color: item.color }} />
                  </div>
                  <h3 className="text-sm font-bold mb-1">{item.title}</h3>
                  <p className="text-xs text-[--ch-text-secondary]">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Wrapping form */}
            <div className="ch-card p-6" data-testid="vig-wrap-form">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
                <Lock size={18} className="text-[--ch-purple]" /> Wrap Your Asset
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="font-label text-[--ch-text-secondary] mb-2 block">Asset Title</label>
                  <input
                    data-testid="vig-title-input"
                    type="text"
                    value={assetTitle}
                    onChange={(e) => setAssetTitle(e.target.value)}
                    placeholder="e.g. Summer Vibes Beat Pack"
                    className="w-full px-4 py-3 rounded-lg bg-[--ch-bg-secondary] border border-[--ch-border] text-white placeholder-gray-500 focus:border-[--ch-purple] focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="font-label text-[--ch-text-secondary] mb-2 block">Asset Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {ASSET_TYPES.map(t => (
                      <button
                        key={t.id}
                        data-testid={`vig-type-${t.id}`}
                        onClick={() => setAssetType(t.id)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          assetType === t.id
                            ? 'bg-[rgba(139,92,246,0.15)] text-[--ch-purple] border border-[--ch-purple]'
                            : 'bg-[--ch-bg-secondary] text-[--ch-text-secondary] border border-[--ch-border] hover:border-gray-500'
                        }`}
                      >
                        <span className="text-lg block mb-0.5">{t.icon}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="font-label text-[--ch-text-secondary] mb-2 block">Description (Optional)</label>
                  <textarea
                    data-testid="vig-description-input"
                    value={assetDescription}
                    onChange={(e) => setAssetDescription(e.target.value)}
                    placeholder="Describe your content for the certificate..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-[--ch-bg-secondary] border border-[--ch-border] text-white placeholder-gray-500 focus:border-[--ch-purple] focus:outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  data-testid="vig-wrap-btn"
                  onClick={handleWrap}
                  disabled={!assetTitle.trim()}
                  className="btn-neon w-full py-4 rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ background: assetTitle.trim() ? 'linear-gradient(135deg, #8B5CF6, #FF00E5)' : undefined }}
                >
                  <Shield size={16} /> Generate .VIG Certificate <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PROCESSING STEP */}
        {step === 'processing' && (
          <div className="animate-fade-in-up text-center" data-testid="vig-processing">
            <div className="ch-card p-10 max-w-md mx-auto" style={{ borderColor: 'rgba(139,92,246,0.3)', boxShadow: '0 0 30px rgba(139,92,246,0.1)' }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ background: 'rgba(139,92,246,0.1)', border: '2px solid rgba(139,92,246,0.3)' }}>
                <Hash size={28} className="text-[--ch-purple] animate-pulse" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-[--ch-purple]" style={{ fontFamily: 'Orbitron' }}>
                WRAPPING ASSET
              </h3>
              <p className="text-sm text-[--ch-text-secondary] mb-6">
                Generating SHA-256 hash and .VIG certificate...
              </p>
              <div className="w-full max-w-xs mx-auto">
                <div className="h-2 bg-[--ch-bg-secondary] rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all duration-200"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #8B5CF6, #FF00E5)',
                      boxShadow: '0 0 12px rgba(139,92,246,0.5)',
                    }} />
                </div>
                <p className="text-sm text-[--ch-purple] font-mono">{Math.round(progress)}%</p>
              </div>
              <div className="mt-6 space-y-1 text-left">
                {[
                  { text: 'Content fingerprinting...', done: progress > 25 },
                  { text: 'SHA-256 hash generation...', done: progress > 50 },
                  { text: 'Timestamp certification...', done: progress > 75 },
                  { text: 'Writing .VIG certificate...', done: progress >= 100 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {item.done ? (
                      <CheckCircle size={14} className="text-[--ch-success]" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border border-gray-600" />
                    )}
                    <span className={item.done ? 'text-[--ch-success]' : 'text-[--ch-text-secondary]'}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* COMPLETE STEP */}
        {step === 'complete' && certificate && (
          <div className="animate-fade-in-up" data-testid="vig-certificate">
            <div className="ch-card p-6 mb-6 text-center" style={{ borderColor: 'rgba(34,197,94,0.3)', boxShadow: '0 0 20px rgba(34,197,94,0.1)' }}>
              <CheckCircle size={32} className="text-[--ch-success] mx-auto mb-3" />
              <h3 className="text-xl font-bold text-[--ch-success] mb-1" style={{ fontFamily: 'Orbitron' }}>
                ASSET WRAPPED
              </h3>
              <p className="text-sm text-[--ch-text-secondary]">
                Your content is now protected with .VIG Protocol
              </p>
            </div>

            {/* Certificate display */}
            <div className="ch-card overflow-hidden" style={{ borderColor: 'rgba(139,92,246,0.3)' }}>
              <div className="p-4 border-b border-[--ch-border] flex items-center justify-between"
                style={{ background: 'rgba(139,92,246,0.05)' }}>
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-[--ch-purple]" />
                  <span className="font-label text-[--ch-purple]">.VIG CERTIFICATE</span>
                </div>
                <span className="text-xs text-[--ch-text-secondary] font-mono">{certificate.certificate_id}</span>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="font-label text-[--ch-text-secondary] mb-1">Asset Title</p>
                    <p className="text-white font-semibold">{certificate.title}</p>
                  </div>
                  <div>
                    <p className="font-label text-[--ch-text-secondary] mb-1">Asset Type</p>
                    <p className="text-white font-semibold capitalize">{certificate.asset_type}</p>
                  </div>
                  <div>
                    <p className="font-label text-[--ch-text-secondary] mb-1">Owner</p>
                    <p className="text-white font-semibold">{certificate.owner}</p>
                  </div>
                  <div>
                    <p className="font-label text-[--ch-text-secondary] mb-1">Wrapped At</p>
                    <p className="text-white font-semibold">{new Date(certificate.wrapped_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* Hash display */}
                <div className="p-4 rounded-lg" style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.15)' }}>
                  <p className="font-label text-[--ch-purple] mb-2 flex items-center gap-1">
                    <Hash size={12} /> SHA-256 HASH
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-white break-all flex-1" data-testid="vig-hash-value">
                      {certificate.vig_hash}
                    </code>
                    <button
                      data-testid="vig-copy-hash"
                      onClick={copyHash}
                      className="p-2 rounded-lg hover:bg-[rgba(139,92,246,0.1)] transition-colors flex-shrink-0"
                    >
                      {copied ? <CheckCircle size={16} className="text-[--ch-success]" /> : <Copy size={16} className="text-[--ch-text-secondary]" />}
                    </button>
                  </div>
                </div>

                {/* VIG Signature */}
                <div className="p-4 rounded-lg" style={{ background: 'rgba(0,240,255,0.03)', border: '1px solid rgba(0,240,255,0.1)' }}>
                  <p className="font-label text-[--ch-cyan] mb-2 flex items-center gap-1">
                    <Fingerprint size={12} /> .VIG SIGNATURE
                  </p>
                  <code className="text-xs font-mono text-[--ch-cyan] break-all" data-testid="vig-signature">
                    {certificate.vig_signature}
                  </code>
                </div>

                <div className="p-3 rounded-lg flex items-start gap-2"
                  style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }}>
                  <AlertTriangle size={14} className="text-[--ch-warning] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[--ch-warning]">
                    This certificate serves as cryptographic proof of content ownership. Store the hash securely.
                    It can be used as evidence in DMCA proceedings under 17 U.S.C. 512.
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-[--ch-border] flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="vig-wrap-another"
                  onClick={resetForm}
                  className="btn-outline-neon px-6 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 flex-1"
                  style={{ borderColor: '#8B5CF6', color: '#8B5CF6' }}
                >
                  <Upload size={14} /> Wrap Another Asset
                </button>
                <button
                  data-testid="vig-view-enforcement"
                  onClick={() => navigate('/enforcement')}
                  className="btn-neon px-6 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 flex-1"
                >
                  View Enforcement <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
